/**
 * onboardingResume
 *
 * Single source of truth for "where should this user go right now?" —
 * every auth entry point (email verify, sign in, OAuth callback,
 * welcome-with-session) funnels through here so we never hardcode a
 * post-auth destination.
 *
 * Mirrors the React Native app's getOnboardingResumeTarget() logic
 * (see SIGNUP_FLOW.md in the RN repo). Status-first — the BE profile's
 * `status` field decides the coarse destination, then org branding
 * decides the fine-grained onboarding step.
 *
 * ALL routing decisions read BE state (userProfile + organization), so
 * a user can start on device A and continue on device B without losing
 * anything past the last successfully-committed step. Optional
 * `ctx.pendingInvite` lets us route invitees whose BE profile hasn't
 * been created yet (invite payload rides on Supabase user_metadata,
 * which is cross-device).
 *
 * Returns:
 *   { path, reason, step? }
 *
 * Callers navigate to `path`. `reason` is useful for logging /
 * debugging. `step` is present when path === '/onboarding' and lets
 * the Onboarding page skip ahead to the correct in-progress step.
 */

// User-profile status values that route the user to a "halted" screen
// rather than into the app. Matches the RN status enum.
const HALTED_STATUSES = new Set(['ONBOARDING_COMPLETED', 'INACTIVE', 'SUSPENDED', 'ARCHIVED']);

/**
 * `/users/register` requires `org_id` in its payload, so Onboarding.jsx
 * sends this zero-UUID as a placeholder BEFORE the real org is created,
 * then PATCHes the profile with the real org_id after
 * `POST /organizations`. Anywhere on the auth path that fetches the org
 * needs to treat this value the same as "no org" — otherwise we'll fire
 * a `GET /organizations/00000000-...` that always 404s.
 */
export const DUMMY_ORG_ID = '00000000-0000-0000-0000-000000000000';

export function hasRealOrgId(userProfile) {
  const id = userProfile?.org_id;
  return !!id && id !== DUMMY_ORG_ID;
}

export function getOnboardingResumeTarget(userProfile, organization, ctx = {}) {
  // 1. No BE user_profile row exists yet.
  //    - Invitee with pending invite context → back to /register/team
  //      so they can finish the invite acceptance (their profile row
  //      gets created there).
  //    - Everyone else → /onboarding step 1 (Profile).
  if (!userProfile?.userprofile_id) {
    if (ctx.pendingInvite) {
      return {
        path: `/register/team?d=${encodeURIComponent(ctx.pendingInvite)}`,
        reason: 'invite-pending',
      };
    }
    return { path: '/onboarding', reason: 'no-profile', step: 'profile' };
  }

  // 2. Status-first routing. Uppercase-normalise defensively — the BE
  //    convention is uppercase but a stray lowercase value shouldn't
  //    dump the user back into onboarding.
  const status = String(userProfile.status || '').toUpperCase();
  if (status === 'ACTIVE') {
    // Super admins live in the Organizations page — sending them
    // to /dashboard drops them on a screen they can't navigate
    // away from (Dashboard/Track/Quotes/etc. are hidden from their
    // sidebar). Route them straight to their landing page instead.
    const role = String(userProfile.role || '').toLowerCase();
    if (role === 'super_admin') {
      return { path: ctx.postActive || '/organizations', reason: 'active-super-admin' };
    }
    return { path: ctx.postActive || '/dashboard', reason: 'active' };
  }
  if (HALTED_STATUSES.has(status)) {
    // /onboarding's existing 'review' step renders the "under review"
    // copy — reuse it rather than adding a new /account-review route.
    // Reason string carries the specific status so callers can
    // localise messaging later if product wants per-state copy.
    return { path: '/onboarding', reason: status.toLowerCase(), step: 'review' };
  }

  // 3. NEW / ONBOARDING_STARTED / legacy null → walk the org to find
  //    the next incomplete step. Profile step is done (we have a
  //    profile row); the remaining owner-flow steps hang off the org.
  if (!hasRealOrgId(userProfile)) {
    // Owner never got past the profile step (never hit
    // POST /organizations) — or their profile still holds the
    // DUMMY_ORG_ID placeholder from register. Resume at profile so
    // the org gets created.
    return { path: '/onboarding', reason: 'no-org', step: 'profile' };
  }

  const branding = organization?.branding || {};
  const isOperator = organization?.type === 'OPERATOR'
    || userProfile?.role === 'operator'; // legacy fallback

  if (isOperator && (!branding.do85Registration || !branding.part135Certification)) {
    return { path: '/onboarding', reason: 'missing-docs', step: 'documents' };
  }
  if (!branding.logo || !branding.color) {
    return { path: '/onboarding', reason: 'missing-branding', step: 'branding' };
  }
  if (!branding.contract) {
    return { path: '/onboarding', reason: 'missing-contract', step: 'contract' };
  }

  // All onboarding steps done but status hasn't advanced to
  // ONBOARDING_COMPLETED yet — either the final PATCH failed or
  // legacy account. Send them to the review step so they can trigger
  // the finalisation from the "Submit for review" button.
  return { path: '/onboarding', reason: 'onboarding-complete', step: 'review' };
}

/**
 * Read the invite context off a Supabase session.
 * TeamRegistration.jsx passes the invite payload to
 * supabase.auth.signUp({ options: { data: { invite: <encodedString> }}}),
 * so `session.user.user_metadata.invite` survives cross-device.
 */
export function readPendingInviteFromSession(session) {
  return session?.user?.user_metadata?.invite || null;
}

/**
 * Read the pre-verify signup context off a Supabase session. SignUp.jsx
 * pushes { first_name, last_name, user_type } into user_metadata so any
 * device that verifies email later can prefill the Profile step.
 * Falls back to splitting the legacy `name` field on the first space.
 */
export function readSignupContextFromSession(session) {
  const meta = session?.user?.user_metadata || {};
  if (meta.first_name || meta.last_name || meta.user_type) {
    return {
      firstName: meta.first_name || '',
      lastName: meta.last_name || '',
      userType: meta.user_type || '',
    };
  }
  if (meta.name && typeof meta.name === 'string') {
    const parts = meta.name.trim().split(/\s+/);
    return {
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' ') || '',
      userType: '',
    };
  }
  return { firstName: '', lastName: '', userType: '' };
}
